import type { PortfolioReport } from '@interfaces/portfolioReport';
import { UserContext } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import type { TFunction } from 'i18next';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { usePermission } from '../../../../hooks/usePermission';
import type { BulkRebalanceReportDetailSelection } from '../detail/reportModal/reportDetailModal';
import type {
  OptimizerAuditRollup,
  OptimizerAuditV2Record,
} from '../types/audit.types';
import type { BulkRebalanceRun } from '../types/run.types';
import type {
  RunReportImpactFilter,
  RunReportPortfolioFilters,
} from '../types/runTable.types';
import { REPORTS_PAGE_SIZE } from '../utils/detailPage/constants';
import {
  resolveProceedDisabledReason,
  resolveShowEmptyRunResults,
  resolveShowZeroSucceededNotice,
} from '../utils/detailPage/detailPagePresentation';
import {
  isDetailSelectionReportPending,
  resolveDetailSelectionReport,
} from '../utils/presentation/resolveDetailSelectionReport';
import { buildReportTickerMap } from '../utils/reports/reportTickerMap';
import { useBulkRebalanceDetailAudits } from './useBulkRebalanceDetailAudits';
import { useBulkRebalanceDetailReports } from './useBulkRebalanceDetailReports';
import { useBulkRebalanceDetailRun } from './useBulkRebalanceDetailRun';
import { useBulkRebalanceProceedToBulkTrader } from './useBulkRebalanceProceedToBulkTrader';
import {
  type UseBulkRebalanceTradeReviewResult,
  useBulkRebalanceTradeReview,
} from './useBulkRebalanceTradeReview';

export type UseBulkRebalanceDetailPageResult = {
  t: TFunction;
  runId: string | undefined;
  run: BulkRebalanceRun | null;
  runLoading: boolean;
  runErrorMessage?: string;
  inProgress: boolean;
  showReports: boolean;
  isUnsupportedDryRun: boolean;
  reports: PortfolioReport[];
  reportsLoading: boolean;
  reportsPage: number;
  reportsPageSize: number;
  reportsTotalCount: number;
  setReportsPage: (page: number) => void;
  canWriteBulkTradeRun: boolean;
  canProceed: boolean;
  proceedDisabledReason?: string;
  proceeding: boolean;
  onProceedToBulkTrader: () => void;
  showZeroSucceededNotice: boolean;
  showEmptyRunResults: boolean;
  auditRollup: OptimizerAuditRollup | null;
  auditRecords: OptimizerAuditV2Record[];
  auditLoading: boolean;
  showProceedButton: boolean;
  failedPortfolioReportsLoading: boolean;
  reportImpactFilter: RunReportImpactFilter;
  setReportImpactFilter: (value: RunReportImpactFilter) => void;
  reportsFullyLoaded: boolean;
  tradeReview: UseBulkRebalanceTradeReviewResult;
  portfolioFilters: RunReportPortfolioFilters;
  setPortfolioFilters: (value: RunReportPortfolioFilters) => void;
  isTradeReviewStep: boolean;
  openTradeReview: () => void;
  closeTradeReview: () => void;
  tickerMap: Record<string, string>;
  activeDetailSelection: BulkRebalanceReportDetailSelection | null;
  detailReportLoading: boolean;
  openReportDetail: (selection: BulkRebalanceReportDetailSelection) => void;
  closeReportDetail: () => void;
};

export const useBulkRebalanceDetailPage =
  (): UseBulkRebalanceDetailPageResult => {
    const { t } = useTranslation(['bulkRebalance']);
    const { runId } = useParams();
    const { activeOrganization } = useContext(UserContext);
    const canWriteBulkTradeRun = usePermission('write:bulk_trade_run');
    const canReadRebalanceBasic = usePermission('read:rebalance_basic');
    const canReadPortfolioOptimizer = usePermission('read:portfolio_optimizer');
    const [reportsPage, setReportsPage] = usePageState(1, 'page');
    const [reportImpactFilter, setReportImpactFilter] =
      useState<RunReportImpactFilter>('all');
    const [portfolioFilters, setPortfolioFilters] =
      useState<RunReportPortfolioFilters>({});
    const [detailStep, setDetailStep] = usePageState('results', 'step');
    const [detailSelection, setDetailSelection] =
      useState<BulkRebalanceReportDetailSelection | null>(null);
    const previousRunIdRef = useRef(runId);

    useEffect(() => {
      if (previousRunIdRef.current === runId) {
        return;
      }

      previousRunIdRef.current = runId;
      setReportsPage(1);
      setReportImpactFilter('all');
      setPortfolioFilters({});
      setDetailSelection(null);
      setDetailStep('results');
    }, [runId, setReportsPage, setDetailStep]);

    const {
      run,
      runLoading,
      runErrorMessage,
      inProgress,
      isUnsupportedDryRun,
      showReports,
      useOptimizedReportsQuery,
      succeededCount,
    } = useBulkRebalanceDetailRun(runId);

    const canReadReports = canReadRebalanceBasic || canReadPortfolioOptimizer;

    const { auditRecords, auditLoading, auditRollup } =
      useBulkRebalanceDetailAudits(
        runId,
        run,
        inProgress,
        canReadPortfolioOptimizer,
      );

    const {
      allRunReports: reports,
      reportsQueryLoading,
      reportsLoading,
      reportsTotalCount,
      reportsFullyLoaded,
      failedPortfolioReportsLoading,
    } = useBulkRebalanceDetailReports({
      runId,
      run,
      showReports,
      canReadReports,
      useOptimizedReportsQuery,
      portfolioFilters,
      reportsPage,
      inProgress,
      organizationId: activeOrganization.id,
      auditRecords,
    });

    const { canProceed, proceeding, onProceedToBulkTrader } =
      useBulkRebalanceProceedToBulkTrader({
        runId,
        run,
        succeededCount,
        canWriteBulkTradeRun,
        organizationId: activeOrganization.id,
      });

    const tradeReview = useBulkRebalanceTradeReview({
      runId,
      run,
      succeededCount,
      reports,
      runCanProceed: canProceed,
    });

    const proceedDisabledReason =
      resolveProceedDisabledReason(
        t,
        run,
        succeededCount,
        canWriteBulkTradeRun,
      ) ?? tradeReview.proceedDisabledReason;

    const showZeroSucceededNotice = resolveShowZeroSucceededNotice(
      run,
      succeededCount,
    );

    const showEmptyRunResults = resolveShowEmptyRunResults({
      showReports,
      inProgress,
      reportsQueryLoading,
      failedPortfolioReportsLoading,
      allRunReportsLength: reports.length,
      auditRecordsLength: auditRecords.length,
      succeededCount,
    });

    const isTradeReviewStep =
      detailStep === 'trades' && tradeReview.showReview && !inProgress;

    const tickerMap = useMemo(() => buildReportTickerMap(reports), [reports]);
    const reportsById = useMemo(
      () => new Map(reports.map((report) => [report.id, report])),
      [reports],
    );
    const activeDetailSelection = useMemo(
      () =>
        detailSelection
          ? resolveDetailSelectionReport(detailSelection, reportsById)
          : null,
      [detailSelection, reportsById],
    );
    const detailReportLoading = isDetailSelectionReportPending(
      activeDetailSelection,
      reportsById,
      failedPortfolioReportsLoading,
    );

    const openTradeReview = () => {
      setDetailSelection(null);
      setDetailStep('trades');
    };

    return {
      t,
      runId,
      run,
      runLoading,
      runErrorMessage,
      inProgress,
      showReports,
      isUnsupportedDryRun,
      reports,
      reportsLoading,
      reportsPage,
      reportsPageSize: REPORTS_PAGE_SIZE,
      reportsTotalCount,
      setReportsPage,
      canWriteBulkTradeRun,
      canProceed,
      proceedDisabledReason,
      proceeding,
      onProceedToBulkTrader,
      showZeroSucceededNotice,
      showEmptyRunResults,
      auditRollup,
      auditRecords,
      auditLoading,
      showProceedButton: tradeReview.showProceedButton,
      failedPortfolioReportsLoading,
      reportImpactFilter,
      setReportImpactFilter,
      reportsFullyLoaded,
      tradeReview,
      portfolioFilters,
      setPortfolioFilters,
      isTradeReviewStep,
      openTradeReview,
      closeTradeReview: () => setDetailStep('results'),
      tickerMap,
      activeDetailSelection,
      detailReportLoading,
      openReportDetail: setDetailSelection,
      closeReportDetail: () => setDetailSelection(null),
    };
  };
